import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import PubSub from '../../../../../services/PubSub';
import { useUIConfig } from "../Context";
import AddNewKeyModal from '../modals/AddNewKeyModal';
import Toolbar from "../../../../controls/Toolbar";
import RenderParams from "./RenderParams";

const CustomKeyObject = ({
    label,
    description,
    keys,
    path,
    initialValue,
    overridesValue,
    disabled
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [translate] = useTranslation();
    const [expanded, setExpanded] = useState(true);

    const { edit, remove, getInitialValue, reset, saveChanges, getOverridesValue } = useUIConfig();
    const [data, setData] = useState({});

    const onChange = useCallback((_, key, value) => {
        edit(path, key, value);
    }, [edit, path]);

    const onCloseModal = useCallback(() => {
        setShowAddModal(false);
    }, []);

    const onAddKey = useCallback((key) => {
        if (initialValue && Object.keys(initialValue).includes(key)) {
            PubSub.publish("show_info", translate("OTM_INVALID_NAME"))
            return;
        }

        edit(path, key, "");
        setShowAddModal(false);
    }, [initialValue, path, edit, translate]);

    useEffect(() => {
        if (!initialValue) {
            setData({});
            return;
        }
        setData(initialValue);
    }, [keys, initialValue]);

    const renderToolbar = useMemo(() => <div className="border-accenture border-bottom">
        <Toolbar
            buttons={[
                {
                    name: 'Add',
                    hidden: disabled,
                    onClick: () => setShowAddModal(true)
                },
                {
                    name: 'ArrowDropDown',
                    hidden: !expanded,
                    onClick: () => setExpanded(!expanded)
                },
                {
                    name: 'ArrowLeft',
                    hidden: expanded,
                    onClick: () => setExpanded(!expanded)
                }
            ]}
            heading={label}
            tooltip={description}
            buttonTooltip={false}
            className='h6'
        />
    </div>, [label, description, disabled, expanded]);

    const renderAccordion = useMemo(() => {
        return <>
            {renderToolbar}
            {expanded &&
                <div className="bg-body p-3 pt-1 border-1 border border-top-0">
                    {keys.length === 0 && <label>{translate("OTM_EMPTY_OBJECT")}</label>}
                    <RenderParams
                        params={keys.map(key => ({
                            ...key,
                            allowRemove: true,
                            initialValue: key.initialValue || getInitialValue(data[key.name], key.defaultValue, key.type),
                            overridesValue: getOverridesValue(overridesValue, key.name)
                        }))}
                        disabled={disabled}
                        currentPath={path}
                        removeCallback={remove}
                        updateCallback={onChange}
                        resetCallback={(paramName) => { reset(path, paramName); saveChanges(); }} />
                </div>
            }
        </>
    }, [data, disabled, keys, path, overridesValue, remove, expanded, getInitialValue, getOverridesValue, renderToolbar, reset, saveChanges, translate, onChange])

    return <>
        <AddNewKeyModal
            show={showAddModal}
            onClose={onCloseModal}
            onAdd={onAddKey}
        />
        {renderAccordion}
    </>
}

export default CustomKeyObject;